/**
 * diagrams.jsx
 * ---------------------------------------------------------------------------
 * The fifteen named hand-drawn SVG diagrams referenced by the CoConsoleX
 * subtabs. All share the same visual language: 1.5–2px round strokes,
 * no fills (except faint area emphasis), no shadows or gradients,
 * Cormorant-Italic captions in slate-blue.
 */

import React from "react";
import { cxTokens as t } from "../../lib/coconsolexTokens.js";
import {
  Figure,
  LawyerSilhouette,
  ServerIcon,
  CloudIcon,
  VaultIcon,
  DiodeArrow,
  HairlineRing,
  CautionFlag,
} from "./symbols.jsx";

const text = (x, y, str, opts = {}) => (
  <text
    x={x}
    y={y}
    textAnchor={opts.anchor || "middle"}
    fontFamily={opts.serif ? "Cormorant Garamond, serif" : "Inter, sans-serif"}
    fontStyle={opts.italic ? "italic" : "normal"}
    fontSize={opts.size || 11}
    fill={opts.color || t.ink}
    letterSpacing={opts.tracking || "0.06em"}
    fontWeight={opts.weight || 400}
  >
    {str}
  </text>
);

/* ---------- I. Privilege Bubble ---------------------------------------- */
export const PrivilegeBubble = () => (
  <Figure
    caption="Inside the bubble by structure, not by contract."
    ariaLabel="A privilege bubble enclosing two lawyers and a server, with cloud AI vendors locked outside."
  >
    <ellipse
      cx="300"
      cy="180"
      rx="220"
      ry="130"
      fill="none"
      stroke={t.privilegeGold}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <ellipse
      cx="300"
      cy="180"
      rx="195"
      ry="110"
      fill="none"
      stroke={t.privilegeGold}
      strokeWidth="0.8"
      strokeDasharray="3 4"
    />
    <LawyerSilhouette x={210} y={210} />
    <LawyerSilhouette x={390} y={210} />
    <ServerIcon x={300} y={210} />
    {text(210, 250, "ENGAGING FIRM", { size: 9, color: t.slate, tracking: "0.18em" })}
    {text(390, 250, "COCONSOLEX", { size: 9, color: t.slate, tracking: "0.18em" })}
    <CloudIcon x={70} y={70} label="Harvey" faded />
    <CloudIcon x={530} y={70} label="CoCounsel" faded />
    <CloudIcon x={70} y={310} label="Lexis+ AI" faded />
    <CloudIcon x={530} y={310} label="ChatGPT" faded />
  </Figure>
);

/* ---------- II. Co-Counsel Triangle ------------------------------------ */
export const CoCounselTriangle = () => (
  <Figure
    caption="Established doctrine. Borrowed architecture. Broader audience."
    ariaLabel="A triangle whose corners are doctrine, architecture, and audience, with CoConsoleX at the center."
  >
    <polygon
      points="300,60 510,300 90,300"
      fill="none"
      stroke={t.ink}
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <line x1="300" y1="60" x2="300" y2="200" stroke={t.rule} strokeWidth="1" />
    <line x1="510" y1="300" x2="300" y2="200" stroke={t.rule} strokeWidth="1" />
    <line x1="90" y1="300" x2="300" y2="200" stroke={t.rule} strokeWidth="1" />
    {text(300, 50, "DOCTRINE", { size: 11, tracking: "0.22em", weight: 600 })}
    {text(518, 318, "ARCHITECTURE", { size: 11, tracking: "0.22em", weight: 600, anchor: "end" })}
    {text(82, 318, "AUDIENCE", { size: 11, tracking: "0.22em", weight: 600, anchor: "start" })}
    <rect x="240" y="180" width="120" height="38" fill={t.cream} stroke={t.ink} strokeWidth="1.2" />
    {text(300, 196, "CoConsole", { size: 13, serif: true, weight: 600 })}
    {text(300, 210, "X", { size: 17, serif: true, weight: 800, color: t.slate })}
  </Figure>
);

/* ---------- IV. Zero-Trust Gate ---------------------------------------- */
export const ZeroTrustGate = () => (
  <Figure
    caption="Default access is none."
    ariaLabel="A hardware key surrounded by three concentric rings labelled identity, device, and matter."
  >
    <HairlineRing cx={320} cy={180} r={140} color={t.rule} />
    <HairlineRing cx={320} cy={180} r={100} color={t.rule} />
    <HairlineRing cx={320} cy={180} r={60} color={t.slate} />
    {/* YubiKey-ish */}
    <rect x="298" y="150" width="44" height="60" fill={t.paper} stroke={t.ink} strokeWidth="1.6" />
    <circle cx="320" cy="190" r="6" fill="none" stroke={t.ink} strokeWidth="1.4" />
    <line x1="320" y1="142" x2="320" y2="150" stroke={t.ink} strokeWidth="1.6" />
    {/* approaching lawyer */}
    <LawyerSilhouette x={120} y={200} />
    <line x1="138" y1="200" x2="170" y2="190" stroke={t.slate} strokeWidth="1" strokeDasharray="3 4" />
    {text(320, 80, "MATTER", { size: 10, tracking: "0.22em", color: t.slate })}
    {text(320, 110, "DEVICE", { size: 10, tracking: "0.22em", color: t.slate })}
    {text(320, 142, "IDENTITY", { size: 10, tracking: "0.22em", color: t.ink, weight: 600 })}
    {text(120, 240, "LAWYER", { size: 9, tracking: "0.18em", color: t.slate })}
  </Figure>
);

/* ---------- V. HSM Vault ----------------------------------------------- */
export const HSMVault = () => (
  <Figure
    caption="Cryptographic walls. Not policy walls."
    ariaLabel="A hardware security module vault holding three matter-specific keys; only one is reachable."
  >
    <rect x="200" y="80" width="240" height="200" fill="none" stroke={t.ink} strokeWidth="1.8" />
    {/* hooks + keys */}
    {[260, 320, 380].map((x, i) => {
      const labels = ["MATTER A", "MATTER B", "MATTER C"];
      return (
        <g key={i}>
          <line x1={x} y1="110" x2={x} y2="140" stroke={t.ink} strokeWidth="1.2" />
          <circle cx={x} cy="155" r="10" fill="none" stroke={t.ink} strokeWidth="1.6" />
          <line x1={x} y1="165" x2={x} y2="200" stroke={t.ink} strokeWidth="1.6" />
          <line x1={x - 4} y1="190" x2={x + 4} y2="190" stroke={t.ink} strokeWidth="1.6" />
          {text(x, 230, labels[i], { size: 9, tracking: "0.18em", color: t.slate })}
        </g>
      );
    })}
    <LawyerSilhouette x={100} y={200} />
    <line x1="118" y1="200" x2="248" y2="155" stroke={t.ink} strokeWidth="1.4" strokeDasharray="2 3" />
    <line x1="118" y1="200" x2="320" y2="155" stroke={t.rule} strokeWidth="1" strokeDasharray="2 6" />
    <line x1="118" y1="200" x2="380" y2="155" stroke={t.rule} strokeWidth="1" strokeDasharray="2 6" />
    {text(100, 240, "AUTHORIZED FOR A", { size: 9, tracking: "0.18em", color: t.slate })}
  </Figure>
);

/* ---------- VI. Ephemeral Flow ----------------------------------------- */
export const EphemeralFlow = () => (
  <Figure
    caption="Ephemeral by default. Auditable by election."
    ariaLabel="Two flows side by side: default ephemeral inference, and on-hold inference writing to a WORM ledger."
  >
    <line x1="300" y1="40" x2="300" y2="320" stroke={t.rule} strokeDasharray="2 4" />
    {text(150, 60, "DEFAULT", { size: 10, tracking: "0.22em", weight: 600 })}
    {text(450, 60, "ON HOLD", { size: 10, tracking: "0.22em", weight: 600 })}
    {/* Default flow */}
    <rect x="60" y="120" width="60" height="40" fill="none" stroke={t.ink} strokeWidth="1.6" />
    {text(90, 145, "PROMPT", { size: 9, tracking: "0.12em" })}
    <line x1="120" y1="140" x2="180" y2="140" stroke={t.ink} strokeWidth="1.4" />
    <rect x="180" y="120" width="60" height="40" fill="none" stroke={t.ink} strokeWidth="1.6" />
    {text(210, 145, "MODEL", { size: 9, tracking: "0.12em" })}
    <line x1="240" y1="140" x2="280" y2="140" stroke={t.ink} strokeWidth="1.4" />
    {[0, 1, 2, 3, 4].map((i) => (
      <circle key={i} cx={120 + i * 30} cy={220} r={2 - i * 0.3} fill={t.rule} />
    ))}
    {text(160, 250, "discarded", { italic: true, serif: true, color: t.slate, size: 13 })}
    {/* On hold flow */}
    <rect x="320" y="120" width="60" height="40" fill="none" stroke={t.ink} strokeWidth="1.6" />
    {text(350, 145, "PROMPT", { size: 9, tracking: "0.12em" })}
    <line x1="380" y1="140" x2="430" y2="140" stroke={t.ink} strokeWidth="1.4" />
    <rect x="430" y="120" width="60" height="40" fill="none" stroke={t.ink} strokeWidth="1.6" />
    {text(460, 145, "MODEL", { size: 9, tracking: "0.12em" })}
    <line x1="380" y1="160" x2="380" y2="220" stroke={t.ink} strokeWidth="1.4" />
    <line x1="460" y1="160" x2="460" y2="220" stroke={t.ink} strokeWidth="1.4" />
    <rect x="340" y="220" width="160" height="60" fill={t.cream} stroke={t.ink} strokeWidth="1.6" />
    {text(420, 245, "WORM LEDGER", { size: 10, tracking: "0.18em", weight: 600 })}
    {text(420, 262, "RFC 3161 timestamp", { size: 9, color: t.slate, tracking: "0.06em" })}
    <g transform="translate(490 250)">
      <rect x="-6" y="-6" width="12" height="12" fill="none" stroke={t.privilegeGold} strokeWidth="1.5" />
      <line x1="0" y1="-6" x2="0" y2="-2" stroke={t.privilegeGold} strokeWidth="1.5" />
    </g>
  </Figure>
);

/* ---------- VII. Hallucination Guard ----------------------------------- */
export const HallucinationGuard = () => (
  <Figure
    caption="Mata v. Avianca, prevented architecturally."
    ariaLabel="An output pipeline gated by a citation-check stage that flags unverified citations."
  >
    {/* Brain */}
    <ellipse cx="100" cy="180" rx="40" ry="32" fill="none" stroke={t.ink} strokeWidth="1.6" />
    <path d="M 70 165 Q 100 145 130 165 M 70 195 Q 100 215 130 195" fill="none" stroke={t.ink} strokeWidth="1.2" />
    {text(100, 230, "MODEL", { size: 9, tracking: "0.18em", color: t.slate })}
    {/* output line */}
    <line x1="140" y1="180" x2="240" y2="180" stroke={t.ink} strokeWidth="1.4" />
    {/* gate */}
    <rect x="240" y="120" width="120" height="120" fill="none" stroke={t.ink} strokeWidth="1.8" />
    {text(300, 110, "CITATION CHECK", { size: 10, tracking: "0.18em", weight: 600 })}
    {/* checked */}
    <line x1="360" y1="150" x2="460" y2="150" stroke={t.ink} strokeWidth="1.4" />
    <polyline points="468,146 472,152 480,142" fill="none" stroke={t.ink} strokeWidth="1.6" />
    {text(490, 154, "verified", { size: 10, color: t.slate, anchor: "start" })}
    {/* unverified */}
    <line x1="360" y1="200" x2="430" y2="200" stroke={t.cautionAmber} strokeWidth="1.4" strokeDasharray="3 3" />
    <CautionFlag x={444} y={200} />
    {text(460, 204, "UNVERIFIED — DO NOT CITE", {
      size: 9,
      tracking: "0.12em",
      color: t.cautionAmber,
      weight: 600,
      anchor: "start",
    })}
  </Figure>
);

/* ---------- VIII. Diode Mirror ----------------------------------------- */
export const DiodeMirror = () => (
  <Figure
    caption="Replication is unidirectional. Ransomware cannot follow."
    ariaLabel="Primary and secondary facilities connected by a one-way data diode; the reverse path is crossed out."
  >
    <rect x="60" y="130" width="160" height="100" fill="none" stroke={t.ink} strokeWidth="1.6" />
    {text(140, 155, "PRIMARY", { size: 11, tracking: "0.22em", weight: 600 })}
    <ServerIcon x={140} y={195} />
    <rect x="380" y="130" width="160" height="100" fill="none" stroke={t.ink} strokeWidth="1.6" />
    {text(460, 155, "SECONDARY", { size: 11, tracking: "0.22em", weight: 600 })}
    <ServerIcon x={460} y={195} />
    <DiodeArrow x1={220} y1={170} x2={380} y2={170} />
    {text(300, 160, "WORM REPLICATION", { size: 9, color: t.diodeGreen, tracking: "0.18em" })}
    {/* crossed-out reverse */}
    <line x1="380" y1="210" x2="220" y2="210" stroke={t.rule} strokeWidth="1" strokeDasharray="2 4" />
    <line x1="290" y1="195" x2="310" y2="225" stroke={t.cautionAmber} strokeWidth="2" />
    <line x1="290" y1="225" x2="310" y2="195" stroke={t.cautionAmber} strokeWidth="2" />
  </Figure>
);

/* ---------- IX. Shredder Chain ----------------------------------------- */
export const ShredderChain = () => (
  <Figure
    caption="NIST 800-88 Destroy. Witnessed."
    ariaLabel="A GPU card flows into a shredder, producing particles smaller than two millimeters; a witness signs the chain of custody."
  >
    {/* GPU card */}
    <rect x="60" y="140" width="100" height="60" fill="none" stroke={t.ink} strokeWidth="1.6" />
    <line x1="70" y1="155" x2="150" y2="155" stroke={t.ink} strokeWidth="1" />
    <line x1="70" y1="170" x2="150" y2="170" stroke={t.ink} strokeWidth="1" />
    <line x1="70" y1="185" x2="150" y2="185" stroke={t.ink} strokeWidth="1" />
    {text(110, 220, "GPU", { size: 10, tracking: "0.22em", color: t.slate })}
    {/* arrow */}
    <line x1="170" y1="170" x2="220" y2="170" stroke={t.ink} strokeWidth="1.4" />
    <polygon points="220,170 212,166 212,174" fill={t.ink} />
    {/* shredder */}
    <path
      d="M 240 130 L 360 130 L 340 180 L 260 180 Z"
      fill="none"
      stroke={t.ink}
      strokeWidth="1.6"
    />
    <line x1="260" y1="155" x2="340" y2="155" stroke={t.ink} strokeWidth="1.2" />
    {[270, 290, 310, 330].map((x, i) => (
      <line key={i} x1={x} y1={180} x2={x - 4} y2={210} stroke={t.ink} strokeWidth="1.2" />
    ))}
    {text(300, 230, "SHREDDER", { size: 10, tracking: "0.22em", color: t.slate })}
    {/* particles */}
    <line x1="370" y1="170" x2="420" y2="170" stroke={t.ink} strokeWidth="1.4" />
    <polygon points="420,170 412,166 412,174" fill={t.ink} />
    {Array.from({ length: 18 }).map((_, i) => (
      <circle
        key={i}
        cx={440 + (i % 6) * 14}
        cy={150 + Math.floor(i / 6) * 18}
        r="1.2"
        fill={t.ink}
      />
    ))}
    {text(478, 220, "≤ 2mm", { size: 11, weight: 600, color: t.ink })}
    {/* witness */}
    <LawyerSilhouette x={130} y={300} />
    <line x1="148" y1="300" x2="220" y2="300" stroke={t.ink} strokeWidth="1.2" />
    <rect x="220" y="285" width="80" height="30" fill="none" stroke={t.ink} strokeWidth="1.4" />
    {text(260, 304, "CHAIN OF CUSTODY", { size: 8, tracking: "0.14em", color: t.slate })}
  </Figure>
);

/* ---------- X. Three Bubbles ------------------------------------------- */
export const ThreeBubbles = () => (
  <Figure
    caption="Coverage stands independently. Like every co-counsel engagement before."
    ariaLabel="Three overlapping insurance circles — malpractice, cyber, and tail — overlapping in a covered center."
  >
    <circle cx="240" cy="180" r="100" fill="none" stroke={t.ink} strokeWidth="1.6" />
    <circle cx="360" cy="180" r="100" fill="none" stroke={t.ink} strokeWidth="1.6" />
    <circle cx="300" cy="280" r="100" fill="none" stroke={t.ink} strokeWidth="1.6" />
    <circle cx="300" cy="220" r="32" fill={t.privilegeGold} fillOpacity="0.18" stroke="none" />
    {text(180, 130, "MALPRACTICE", { size: 11, tracking: "0.18em", weight: 600 })}
    {text(420, 130, "CYBER", { size: 11, tracking: "0.18em", weight: 600 })}
    {text(300, 360, "TAIL", { size: 11, tracking: "0.18em", weight: 600 })}
    {text(300, 226, "COVERED", { size: 11, tracking: "0.22em", weight: 700, color: t.privilegeGold })}
  </Figure>
);

/* ---------- XI. Audit Facility ----------------------------------------- */
export const AuditFacility = () => (
  <Figure
    caption="An OCC examiner can knock on this door."
    ariaLabel="An auditor walks into a physical facility while a parallel cloud icon shows a closed gate."
  >
    {/* Building */}
    <rect x="40" y="120" width="240" height="180" fill="none" stroke={t.ink} strokeWidth="1.8" />
    <polygon points="40,120 160,60 280,120" fill="none" stroke={t.ink} strokeWidth="1.6" />
    {/* door */}
    <rect x="100" y="220" width="40" height="80" fill="none" stroke={t.ink} strokeWidth="1.4" />
    <line x1="100" y1="220" x2="80" y2="220" stroke={t.ink} strokeWidth="1.4" />
    {/* server inside */}
    <ServerIcon x={210} y={250} />
    <LawyerSilhouette x={170} y={280} />
    {/* auditor */}
    <LawyerSilhouette x={50} y={280} />
    <rect x="40" y="295" width="20" height="14" fill="none" stroke={t.slate} strokeWidth="1" />
    {text(160, 110, "PHYSICAL FACILITY", { size: 10, tracking: "0.22em", weight: 600 })}
    {/* divider */}
    <line x1="320" y1="80" x2="320" y2="320" stroke={t.rule} strokeDasharray="2 4" />
    {/* cloud closed */}
    <CloudIcon x={440} y={170} />
    <rect x="412" y="200" width="56" height="44" fill="none" stroke={t.ink} strokeWidth="1.6" />
    <circle cx="440" cy="222" r="8" fill="none" stroke={t.ink} strokeWidth="1.4" />
    {text(440, 270, "CLOUD VENDOR", { size: 10, tracking: "0.18em", color: t.slate })}
    {text(440, 286, "no entry", { italic: true, serif: true, color: t.slate, size: 13 })}
    <text x="500" y="220" fontSize="22" fill={t.cautionAmber}>?</text>
  </Figure>
);

/* ---------- XII. Hub & Spoke ------------------------------------------- */
export const HubSpoke = () => (
  <Figure
    caption="Independent regional instances. No data crosses borders."
    ariaLabel="A globe with five regional nodes for US, EU, UK, Singapore, and Tokyo, each isolated."
  >
    <ellipse cx="300" cy="180" rx="200" ry="120" fill="none" stroke={t.rule} strokeWidth="1.2" />
    <ellipse cx="300" cy="180" rx="200" ry="60" fill="none" stroke={t.rule} strokeWidth="0.8" />
    <ellipse cx="300" cy="180" rx="80" ry="120" fill="none" stroke={t.rule} strokeWidth="0.8" />
    {[
      { x: 160, y: 140, label: "US" },
      { x: 320, y: 100, label: "EU" },
      { x: 400, y: 130, label: "UK" },
      { x: 470, y: 220, label: "TOKYO" },
      { x: 420, y: 260, label: "SINGAPORE" },
    ].map((n, i) => (
      <g key={i}>
        <circle cx={n.x} cy={n.y} r="22" fill={t.cream} stroke={t.ink} strokeWidth="1.6" />
        <rect x={n.x - 4} y={n.y - 4} width="8" height="8" fill="none" stroke={t.ink} strokeWidth="1.2" />
        {text(n.x, n.y + 38, n.label, { size: 9, tracking: "0.18em", weight: 600 })}
      </g>
    ))}
  </Figure>
);

/* ---------- XIII. Crypto Shred ----------------------------------------- */
export const CryptoShred = () => (
  <Figure
    caption="Crypto-shredding. The key dies, the data follows."
    ariaLabel="A key inside an HSM vault breaks into fragments along a 30/60-day destruction timeline."
  >
    <VaultIcon x={180} y={150} />
    {/* Key fragmenting */}
    <line x1="220" y1="150" x2="280" y2="150" stroke={t.ink} strokeWidth="1.4" strokeDasharray="2 3" />
    {[0, 1, 2, 3, 4].map((i) => (
      <g key={i}>
        <polygon
          points={`${300 + i * 28},${140 - i * 4} ${314 + i * 28},${148 - i * 4} ${308 + i * 28},${156 - i * 4} ${294 + i * 28},${152 - i * 4}`}
          fill="none"
          stroke={t.ink}
          strokeWidth="1.4"
          opacity={1 - i * 0.18}
        />
      </g>
    ))}
    {text(180, 220, "HSM VAULT", { size: 10, tracking: "0.22em", weight: 600 })}
    {/* Timeline */}
    <line x1="60" y1="290" x2="540" y2="290" stroke={t.ink} strokeWidth="1.4" />
    <circle cx="180" cy="290" r="5" fill={t.ink} />
    <circle cx="420" cy="290" r="5" fill={t.ink} />
    {text(180, 318, "30-DAY RETURN WINDOW", { size: 9, tracking: "0.18em", color: t.slate })}
    {text(420, 318, "60-DAY CERTIFICATE", { size: 9, tracking: "0.18em", color: t.slate })}
    {text(420, 332, "OF DESTRUCTION", { size: 9, tracking: "0.18em", color: t.slate })}
  </Figure>
);

/* ---------- XIV. Tiered Access ----------------------------------------- */
export const TieredAccess = () => (
  <Figure
    caption="Same doctrine. Same protection. Different scale."
    ariaLabel="Three stacked tiers — dedicated, shared with HSM isolation, and per-matter — each ringed by an identical privilege bubble."
  >
    {[
      { y: 90, label: "TIER 1 · Dedicated infrastructure · AmLaw 100", w: 360 },
      { y: 170, label: "TIER 2 · Shared with HSM isolation · Mid-market", w: 360 },
      { y: 250, label: "TIER 3 · Per-matter referral · Solo / Small", w: 360 },
    ].map((tier, i) => (
      <g key={i}>
        <rect
          x={300 - tier.w / 2}
          y={tier.y}
          width={tier.w}
          height="50"
          fill={t.paper}
          stroke={t.ink}
          strokeWidth="1.6"
        />
        <ellipse
          cx="300"
          cy={tier.y + 25}
          rx="200"
          ry="34"
          fill="none"
          stroke={t.privilegeGold}
          strokeWidth="1.6"
        />
        {text(300, tier.y + 30, tier.label, { size: 11, tracking: "0.12em", weight: 500 })}
      </g>
    ))}
  </Figure>
);

/* ---------- XV. Concentric Firewall ------------------------------------ */
export const ConcentricFirewall = () => (
  <Figure
    caption="The standard, written in advance."
    ariaLabel="Three concentric rings labelled advisory validation, conference presence, and procurement specification, with CoConsoleX at the centre."
  >
    <HairlineRing cx={300} cy={180} r={150} color={t.rule} />
    <HairlineRing cx={300} cy={180} r={105} color={t.slate} />
    <HairlineRing cx={300} cy={180} r={60} color={t.ink} />
    {/* outer = advisor silhouettes */}
    {[40, 140, 240, 340, 440, 540].map((x, i) => (
      <g key={i} transform="translate(0 0)">
        <LawyerSilhouette x={x} y={180 + (i % 2 === 0 ? -130 : 130)} color={t.slate} />
      </g>
    ))}
    {/* podium */}
    <rect x="282" y="120" width="36" height="22" fill="none" stroke={t.slate} strokeWidth="1.4" />
    <line x1="290" y1="120" x2="290" y2="100" stroke={t.slate} strokeWidth="1" />
    {/* inner doc */}
    <rect x="276" y="160" width="48" height="60" fill={t.cream} stroke={t.ink} strokeWidth="1.6" />
    {[170, 180, 190, 200, 210].map((y, i) => (
      <line key={i} x1="282" y1={y} x2="318" y2={y} stroke={t.ink} strokeWidth="0.8" />
    ))}
    <circle cx="312" cy="214" r="8" fill="none" stroke={t.privilegeGold} strokeWidth="1.4" />
    {text(300, 60, "ADVISORY VALIDATION", { size: 10, tracking: "0.22em", color: t.slate, weight: 600 })}
    {text(300, 95, "CONFERENCE PRESENCE", { size: 9, tracking: "0.22em", color: t.slate })}
    {text(300, 248, "PROCUREMENT SPEC", { size: 9, tracking: "0.22em", color: t.ink, weight: 600 })}
  </Figure>
);
